/**
 * Shared utility for applying AccountGetInfo user settings to local storage.
 * Used by both DebugView (manual download) and the login flow (automatic).
 */

import type { AccountGetInfoResponseDto } from '../types/api/service';
import type { AccountUserSettingDto } from '../types/api/service';
import { appConfig } from '../config/configManager';

/**
 * Maps AccountUserSettingDto.Name values to their corresponding storage keys.
 * Must stay in sync with the setting names used in SettingsView.syncPreferences.
 */
export const SETTING_NAME_TO_STORAGE_KEY: Record<string, string> = {
  track_prescriptions:           'wellcheck_track_prescriptions',
  track_referrals:               'wellcheck_track_referrals',
  track_vaccinations:            'wellcheck_vaccine_info_enabled',
  track_height_weight:           'wellcheck_track_height_weight',
  measurement_units:             'wellcheck_measurement_units',
  language:                      'language',
  milestone_staleness_threshold: 'milestone_staleness_threshold',
  concern_freshness_threshold:   'concern_freshness_threshold',
  action_freshness_threshold:    'action_freshness_threshold',
  auto_signout_timeout:          'auto_signout_timeout',
  wellcheck_schedule_id:         'wellcheck_selected_schedule_id',
  vaccine_schedule_id:           'wellcheck_selected_vaccine_schedule_id',
};

/** Default storage values written before any AccountUserSettingDto entries are applied. */
export const PREF_STORAGE_DEFAULTS: Record<string, string> = {
  wellcheck_track_prescriptions:  'true',
  wellcheck_track_referrals:      'true',
  wellcheck_vaccine_info_enabled: 'true',
  wellcheck_track_height_weight:  'true',
  wellcheck_measurement_units:    'english',
  language:                       'en',
  milestone_staleness_threshold:  '1month',
  concern_freshness_threshold:    '1month',
  action_freshness_threshold:     '1month',
  // Overridden per-SRA once the signed-in SRA record loads (see SraTimeout.applySraTimeoutToLocalStorage).
  auto_signout_timeout:           '1hour',
};

async function writeDefaultPreferenceValues(storage: any): Promise<void> {
  for (const [key, defaultVal] of Object.entries(PREF_STORAGE_DEFAULTS)) {
    try { await storage.setItem(key, defaultVal); } catch {}
  }
  // Remove schedule IDs so SettingsView re-selects the first available entry
  try { await storage.removeItem('wellcheck_selected_schedule_id'); } catch {}
  try { await storage.removeItem('wellcheck_selected_vaccine_schedule_id'); } catch {}
}

/**
 * Applies a raw AccountUserSettingDto[] list to local storage.
 * Intended for login responses that now include Settings directly.
 */
export async function applyUserSettingsFromList(
  settings: AccountUserSettingDto[] | undefined,
  storage: any
): Promise<{ appliedCount: number }> {
  await writeDefaultPreferenceValues(storage);

  let appliedCount = 0;
  for (const setting of settings ?? []) {
    const storageKey = SETTING_NAME_TO_STORAGE_KEY[setting.Name];
    if (storageKey && setting.Value != null) {
      try {
        await storage.setItem(storageKey, String(setting.Value));
        appliedCount++;
      } catch {}
    } else {
      console.warn('applyUserSettingsFromList: unknown setting name:', setting.Name);
    }
  }

  try { await storage.removeItem('preferences_dirty_flag'); } catch {}
  console.log(`✅ applyUserSettingsFromList: reset to defaults then applied ${appliedCount} setting(s)`);
  return { appliedCount };
}

/**
 * After a successful account_get_info fetch:
 * 1. Resolve the logged-in user's AccountUserDto by matching the stored user_name.
 * 2. Reset all known preference storage keys to their defaults.
 * 3. Apply each AccountUserSettingDto from that user's settings list.
 * 4. Clear the preferences_dirty_flag so the Sync button is hidden.
 *
 * Non-fatal — individual storage failures are silently ignored so that a
 * settings-sync problem never blocks the login flow.
 *
 * @returns The number of settings applied and the matched user name.
 */
export async function applyUserSettingsFromDto(
  dto: AccountGetInfoResponseDto,
  storage: any
): Promise<{ appliedCount: number; userName: string | null }> {
  // Identify the logged-in user by the stored user_name
  let storedUserName: string | null = null;
  try { storedUserName = await storage.getItem('user_name'); } catch {}

  const loggedInUser = (dto.accountusers ?? []).find(
    u => storedUserName && u.Name?.trim().toLowerCase() === storedUserName.trim().toLowerCase()
  ) ?? null;

  if (!loggedInUser) {
    console.warn('⚠️ applyUserSettingsFromDto: no matching AccountUserDto found for user_name:', storedUserName);
  }

  // Step 1 — write defaults for all preference storage keys
  await writeDefaultPreferenceValues(storage);

  // Step 2 — apply settings from the matched user's DTO
  let appliedCount = 0;
  if (loggedInUser?.settings?.length) {
    for (const setting of loggedInUser.settings) {
      const storageKey = SETTING_NAME_TO_STORAGE_KEY[setting.Name];
      if (storageKey && setting.Value != null) {
        try {
          await storage.setItem(storageKey, String(setting.Value));
          appliedCount++;
        } catch {}
      } else {
        console.warn('applyUserSettingsFromDto: unknown setting name:', setting.Name);
      }
    }
  }

  // Step 3 — apply premium tier to in-memory config so all views reflect it immediately
  const tier = dto.IsPremium === '1' ? 'premium' : 'standard';
  appConfig.updateConfig({ tier });
  console.log(`✅ applyUserSettingsFromDto: set appConfig.tier to "${tier}" (IsPremium=${dto.IsPremium})`);

  // Step 4 — clear dirty flag since we are now in sync with the backend
  try { await storage.removeItem('preferences_dirty_flag'); } catch {}

  console.log(
    `✅ applyUserSettingsFromDto: reset to defaults then applied ${appliedCount} setting(s) for user "${storedUserName}"`
  );
  return { appliedCount, userName: storedUserName };
}
